<template>
  <Loader
    :value="promise"
    :message="$t('loading', { resource: $tc('contact', 2).toLowerCase() }) + '...'"
    class="contacts"
  >
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #actions>
          <Actions placement="contacts" :query="{ filter, sort }" />
          <DropdownComponent>
            <ButtonComponent :icon="['fal', 'plus']" :label="$tc('create')" theme="primary" />
            <template #dropdown>
              <ButtonComponent
                tag="router-link"
                :to="{
                  name: 'contact-create',
                  query: { returnTo: $route.path },
                }"
                :icon="['fal', 'plus-circle']"
                :label="$tc('contact')"
              />
              <ButtonComponent
                tag="router-link"
                :label="$tc('import')"
                :to="{ name: 'import', params: { type: 'contact' } }"
                :icon="['fal', 'file-import']"
              />
            </template>
          </DropdownComponent>
        </template>
      </HeaderComponent>
      <div class="contacts__filter row between">
        <FilterComponent
          v-model="filter"
          :default="defaultFilter"
          :items="[
            'search',
            'residentials',
            'residentialTemplates',
            'tags',
            'types',
            'select:references',
            'checklist:upsell',
          ]"
          :options="filterOptions"
        >
          <template #upsell="{ items }">{{ items.map(i => $t(i ? 'yes' : 'no')).join('/') }}</template>
        </FilterComponent>
        <SortComponent v-model="sort" :items="sortableBy" />
      </div>

      <DataTable
        v-if="items.length"
        sticky="var(--sticky-contacts-header)"
        :selected.sync="selected"
        :sort-by.sync="sort"
        :sortable-by="sortableBy.map(s => s.key)"
        :value="items"
        :row-wrapper="
          item => ({
            component: 'router-link',
            props: { to: { name: 'contact-dashboard', params: { id: item.id } } },
          })
        "
        :columns="[
          { id: 'name', label: $tc('name'), size: '1fr' },
          { id: 'isInterestedIn', label: $tc('isInterestedIn') },
          { id: 'upsell', label: $tc('contactUpsellShort') },
          { id: 'tag', label: $tc('tag', 2) },
          { id: 'type', label: $tc('type') },
        ]"
      >
        <template #name="{ item }">
          <ContactCard :value="item" theme="list" />
        </template>
        <template #type="{ item }">
          <Badge :value="$tc(item.type, 1)" :theme="item.type == 'customer' ? 'secondary' : 'default'" />
        </template>
        <template #isInterestedIn="{ item }">
          <div class="row g-1">
            <Badge v-for="scope in isInterestedIn(item.leads).slice(0, 3)" :key="scope" :value="scope" />
            <span v-if="isInterestedIn(item.leads).length > 3">...</span>
            <span v-if="!item.leads.length" class="fade">—</span>
          </div>
        </template>
        <template #upsell="{ item }">
          <span v-if="item.upsell">{{ $t('yes') }}</span>
        </template>
        <template #tag="{ item }">
          <div class="row g-1">
            <Badge v-for="tag in item.tags" :key="tag" :value="tag" />
            <span v-if="!item.tags.length" class="fade">—</span>
          </div>
        </template>
        <template #select-actions>
          <ButtonComponent :label="$t('sendMessage')" :icon="['fal', 'comment']" @click="showModal = true" />
        </template>
      </DataTable>

      <InfiniteLoading @infinite="next" :identifier="infiniteId">
        <template #spinner>
          <div class="infinite-loading-spinner">
            <span class="infinite-loading-spinner--text">{{ $t('loading', { resource: '...' }) }}</span>
            <FontAwesomeIcon
              class="infinite-loading-spinner--spinner"
              :icon="['fal', 'spinner-third']"
              spin
              size="2x"
            />
          </div>
        </template>
        <template #no-more>{{ $t('noMore', { x: $tc('contact', 2).toLowerCase() }) }}</template>
        <template #no-results>
          <EmptyState
            :title="$t('noX', { x: $tc('contact', 2).toLowerCase() })"
            :icon="['fal', 'address-card']"
            :subtitle="$t('filterEmptyState')"
          />
        </template>
      </InfiniteLoading>
    </div>
    <CommentModal :show.sync="showModal" :promise="commentPromise" @onSuccess="showModal = false" @submit="comment">
      <div class="mb-2">
        <div class="mb-1">{{ $tc('receiver', 2) }}</div>
        <Avatar :value="selected.map(({ name, avatar: src }) => ({ src, name }))" :max="8" />
      </div>
    </CommentModal>
  </Loader>
</template>

<script>
import CommentApi from '@/api/Comment'
import API from '@/api/Contact'
import { Debounce } from '@/utils'
import Pagination from '@/mixins/Pagination'

import { InputComponent } from 'vue-elder-input'
import { DropdownComponent } from 'vue-elder-dropdown'
import HeaderComponent from '@/components/Header'
import FilterMixin from '@/components/Filter'
import SortComponent from '@/components/Sort'
import Actions from '@/components/Actions'
import Card from '@/components/Card'
import EmptyState from '@/components/EmptyState'
import DataTable from '@/components/DataTable'
import Badge from '@/components/Badge'
import Avatar from '@/components/Avatar'
import ContactCard from '@/components/Contact/Card'
import CommentModal from '@/components/Comment/Modal'
import InfiniteLoading from 'vue-infinite-loading'
import tracker from '@/tracking'

export default {
  mixins: [
    FilterMixin({ default: { search: '', residentials: undefined, residentialTemplates: undefined } }),
    Pagination,
  ],

  watch: {
    filter: {
      handler() {
        this.userUpdate('filter')
      },
      deep: true,
    },
    sort: {
      handler() {
        this.userUpdate('sort')
      },
      deep: true,
    },
    'item.page': {
      handler(newVal) {
        if (!newVal || newVal === 1) return
        tracker.emit('ContactsList:paginate', this.item)
      },
    },
  },
  data() {
    return {
      sort: { name: 1 },
      sortableBy: [
        { key: 'email', label: this.$t('email') },
        { key: 'name', label: this.$t('name') },
      ],
      showModal: false,
      commentPromise: null,
      promise: null,
    }
  },
  computed: {
    breadcrumb() {
      return []
    },
    items() {
      return this.item.docs
    },
    filterOptions() {
      return {
        tags: { items: '/api/contact/tags' },
        upsell: {
          label: this.$t('contactUpsellShort'),
          transformFilter: {
            set(v) {
              if (!v) return { upsell: true }
              if (!v.length || v.length === 2) return { upsell: undefined }
              if (v.includes(true)) return { upsell: true }
              return { upsell: false }
            },
            get(v) {
              if (v.upsell === undefined) return [true, false]
              if (!(v.upsell instanceof Array)) return v.upsell
              if (!v.upsell.length) return [true, false]
              return v.upsell
            },
          },
          items: [
            {
              value: true,
              label: this.$tc('yes'),
            },
            {
              value: false,
              label: this.$tc('no'),
            },
          ],
        },

        search: {
          placeholder: [this.$t('name'), this.$t('email')].map((v, i) => (i ? v.toLowerCase() : v)).join(', '),
        },
        references: {
          items: '/api/resource/project',
          label: this.$tc('project', 2),
          multiple: true,
          search: true,
        },
      }
    },
  },
  methods: {
    fetch() {
      if (this.item.pages === 0 || (this.item.pages && this.item.page === this.item.pages)) return Promise.resolve()

      this.promise = API.get(
        {
          paginate: this.paginate,
          filter: this.filter,
          sort: this.sort,
        },
        {
          query: 'ContactsList',
        },
      )

      return this.promise
    },

    userUpdate: Debounce(function (v) {
      if (Object.values(this[v]).filter(v => (Array.isArray(v) ? v.length : Boolean(v))).length)
        tracker.emit(`ContactsList:${v}`, { ...this[v] })

      this.reset()
      this.fetch()
    }, 200),

    isInterestedIn(leads) {
      return leads.map(l => l.scopes.map(s => s.name)).flat()
    },

    comment(data) {
      tracker.emit('multiple:comment', {})

      this.commentPromise = CommentApi.mutate(
        {
          data: this.selected.map(s => ({
            reference: { onModel: 'Contact', ref: s.id },
            ...data,
          })),
        },
        { mutation: 'CommentsCreate', transformPayload: false },
      )
    },
  },
  metaInfo() {
    return {
      title: this.$tc('contact', 2),
    }
  },
  components: {
    Card,
    HeaderComponent,
    SortComponent,
    InputComponent,
    Actions,
    DropdownComponent,
    EmptyState,
    DataTable,
    ContactCard,
    CommentModal,
    Badge,
    Avatar,
    InfiniteLoading,
  },
}
</script>

<style lang="scss">
.contacts {
  $space: 1rem;
  &__filter {
    margin-bottom: $space;
  }
  --sticky-contacts-header: calc(var(--header-height) - #{$space} - 2px);
  background-color: $background-grey;
}
</style>
